/**
 * Damage mitigation.
 *
 * RULE DCC-DMG-002 - Rules for Survival p.42. The order is fixed:
 *   1. subtract Damage Resistance (DR), which applies to all damage types;
 *   2. apply Elemental Resistance, which halves damage of that type;
 *   3. apply Vulnerability, which doubles damage of that type;
 *   4. apply Immunity, which reduces the damage to zero and also cancels any
 *      additional effects such as Debuffs.
 */

/** RULE DCC-DMG-001 - the damage types (Rules p.42). */
export const DAMAGE_TYPES = [
  'acid',
  'bludgeoning',
  'electric',
  'fire',
  'force',
  'holy',
  'ice',
  'necrotic',
  'piercing',
  'poison',
  'psychic',
  'slashing',
  'sonic',
] as const;

export type DamageType = (typeof DAMAGE_TYPES)[number];

export function isDamageType(value: unknown): value is DamageType {
  return typeof value === 'string' && (DAMAGE_TYPES as readonly string[]).includes(value);
}

export interface Mitigation {
  /** Flat reduction against every damage type. Gained from armour, magic and Buffs. */
  damageResistance: number;
  elementalResistances: readonly DamageType[];
  vulnerabilities: readonly DamageType[];
  immunities: readonly DamageType[];
}

export const NO_MITIGATION: Mitigation = {
  damageResistance: 0,
  elementalResistances: [],
  vulnerabilities: [],
  immunities: [],
};

export interface DamageStep {
  label: string;
  /** Running damage total after this step. */
  running: number;
}

export interface MitigationResult {
  final: number;
  steps: DamageStep[];
  /** True when Immunity applied, which also cancels any attached effects. */
  effectsCancelled: boolean;
}

export interface DamageInput {
  amount: number;
  /**
   * Null for damage the source states without a type. Rules for Survival p.45
   * gives Blood Trail and Drowning as plain "1d6+F". Damage Resistance still
   * applies to untyped damage; no elemental resistance, vulnerability or
   * immunity can match it.
   */
  type: DamageType | null;
  /**
   * Some damage explicitly bypasses DR. The GLOBAL rule is Rules for Survival
   * p.46: "Damage from Debuffs bypasses DR, but other resistances and
   * immunities apply as normal." The worked example on p.41 illustrates it.
   */
  ignoresDamageResistance?: boolean;
}

export function mitigateDamage(input: DamageInput, mitigation: Mitigation): MitigationResult {
  if (!Number.isInteger(input.amount) || input.amount < 0) {
    throw new Error(`Damage must be a whole number of at least 0, received ${input.amount}`);
  }

  const typeLabel = input.type ?? 'untyped';
  const steps: DamageStep[] = [{ label: `Incoming ${typeLabel} damage`, running: input.amount }];
  let running = input.amount;

  if (input.ignoresDamageResistance) {
    steps.push({ label: 'Damage Resistance does not apply', running });
  } else if (mitigation.damageResistance !== 0) {
    running = Math.max(0, running - mitigation.damageResistance);
    steps.push({ label: `Damage Resistance -${mitigation.damageResistance}`, running });
  }

  if (input.type !== null && mitigation.elementalResistances.includes(input.type)) {
    running = Math.floor(running / 2);
    steps.push({ label: `${titleCase(typeLabel)} Resistance halves`, running });
  }

  if (input.type !== null && mitigation.vulnerabilities.includes(input.type)) {
    running = running * 2;
    steps.push({ label: `${titleCase(typeLabel)} Vulnerability doubles`, running });
  }

  const immune = input.type !== null && mitigation.immunities.includes(input.type);
  if (immune) {
    running = 0;
    steps.push({ label: `${titleCase(typeLabel)} Immunity reduces to 0`, running });
  }

  return { final: running, steps, effectsCancelled: immune };
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
